import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONFIRM_REQUIRED, SERVICE_CLASSES, SERVICE_CLASS_LABELS, type DispatchResult, type DispatchState, type MailOutcome, type Order, type ServiceClass } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './disposition.css';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const dayDe = (iso: string) => iso.split('-').reverse().join('.');

const MAIL_TEXT: Record<MailOutcome, string> = {
  sent: 'gesendet',
  already: 'war schon gesendet',
  no_email: 'nicht gesendet: Der Kunde hat keine E-Mail-Adresse',
  no_sender: 'nicht gesendet: Der E-Mail-Versand ist nicht eingerichtet',
  failed: 'nicht gesendet: Der Mailserver hat die Nachricht abgelehnt',
  not_requested: 'nicht angefordert',
};

/**
 * Disposition am Auftrag (KON-238): Der Dispatcher weist den Auftrag einem Fahrer und einem Fahrzeug zu. Zur Wahl stehen nur Fahrer mit veröffentlichter Schicht am Tag der Abholung. Mit dem
 * Zuweisen wird der Auftrag „Geplant“, der Kunde bekommt die Bestätigung und die Angaben zu Fahrer und Fahrzeug per E-Mail (jede Mail geht nur einmal je Stand). Gedacht für Backoffice und
 * Mobile App: Die App ruft dieselben Endpunkte auf (`GET` und `POST /orders/:id/dispatch`).
 */
export function OrderDispatch({ order }: { order: Order }) {
  const { can, hasModule, session } = useSession();
  const client = useQueryClient();
  const allowed = session?.tenant.industry === 'chauffeur' && hasModule('orders') && can('orders.plan') && hasModule('fleet') && ['open', 'planned', 'in_progress'].includes(order.status);
  const state = useQuery({ queryKey: ['dispatch', order.id, order.updatedAt], queryFn: async () => (await api<{ data: DispatchState }>('GET', `/orders/${order.id}/dispatch`)).data, enabled: allowed });
  const [employeeId, setEmployeeId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [notify, setNotify] = useState(true);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const d = state.data;

  // Vorbelegung aus dem Stand: die bisherige Zuweisung oder der Vorschlag aus der Anfrage
  useEffect(() => {
    if (!d) return;
    // Eine Zuweisung an einem anderen Tag als dem Leistungsdatum bleibt nicht vorgewählt: Fahrer und Fahrzeug sind für den neuen Tag neu zu wählen
    // Ein Fahrer, der die Fahrt selbst angenommen hat, ist vorgewählt: Es fehlt dann nur noch das Fahrzeug
    setEmployeeId(d.current && !d.current.outdated ? (d.current.employeeId ?? '') : (d.driver?.employeeId ?? ''));
    setVehicleId(d.current && !d.current.outdated ? d.current.vehicleId : '');
    setStartsAt(d.suggestedStart ?? '');
    setEndsAt(d.suggestedEnd ?? '');
  }, [d?.orderId, d?.current?.assignmentId, d?.current?.outdated, d?.suggestedStart, d?.suggestedEnd]);

  const assign = useMutation({
    mutationFn: async (confirmWarnings: boolean) => (await api<{ data: DispatchResult }>('POST', `/orders/${order.id}/dispatch`, { employeeId, vehicleId, startsAt, endsAt, notifyCustomer: notify, confirmWarnings })).data,
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['order', order.id] });
      client.invalidateQueries({ queryKey: ['orders'] });
      client.invalidateQueries({ queryKey: ['assignments'] });
      client.invalidateQueries({ queryKey: ['dispatch', order.id] });
      client.setQueryData(['dispatch', order.id, order.updatedAt], res.state);
      const parts = [`Bestätigung ${MAIL_TEXT[res.mails.confirmation]}`, `Angaben zu Fahrer und Fahrzeug ${MAIL_TEXT[res.mails.driverInfo]}`];
      const warn = [res.mails.confirmation, res.mails.driverInfo].some((m) => m === 'no_email' || m === 'no_sender' || m === 'failed');
      setNotice({ ok: !warn, text: `Der Auftrag ist zugewiesen. ${notify ? parts.join('; ') + '.' : 'Der Kunde wurde nicht informiert.'}${warn ? ' Bitte informieren Sie den Kunden selbst.' : ''}` });
    },
    onError: (e, confirmed) => {
      // Eine Warnung ist kein Verbot: Der Dispatcher sieht sie und entscheidet, ob die Zuweisung trotzdem geschieht
      if (!confirmed && e instanceof ApiError && e.code === CONFIRM_REQUIRED) {
        if (window.confirm([...Object.values(e.fields), 'Trotzdem zuweisen?'].join('\n\n'))) assign.mutate(true);
        return;
      }
      setNotice({ ok: false, text: errorText(e, 'Die Zuweisung hat nicht geklappt.') });
    },
  });

  const setClass = useMutation({
    mutationFn: async (serviceClass: ServiceClass | null) => (await api<{ data: DispatchState }>('PUT', `/orders/${order.id}/dispatch/service-class`, { serviceClass })).data,
    onSuccess: (next) => {
      client.setQueryData(['dispatch', order.id, order.updatedAt], next);
      client.invalidateQueries({ queryKey: ['dispatch', order.id] });
      client.invalidateQueries({ queryKey: ['orders'] });
      // Passt das Fahrzeug nicht mehr, ist die Wahl neu zu treffen
      setVehicleId((cur) => (next.vehicles.some((v) => v.vehicleId === cur && v.free) ? cur : ''));
      setNotice(null);
    },
    onError: (e) => setNotice({ ok: false, text: errorText(e, 'Die Klasse ließ sich nicht ändern.') }),
  });

  if (!allowed) return null;
  if (state.isLoading) return <section className="card"><h2>Disposition</h2><p className="muted">Wird geladen …</p></section>;
  if (!d) return <section className="card"><h2>Disposition</h2><div className="alert alert--error" role="alert">Die Disposition konnte nicht geladen werden.</div></section>;

  const driver = d.drivers.find((x) => x.employeeId === employeeId);
  const vehicle = d.vehicles.find((x) => x.vehicleId === vehicleId);
  const currentMismatch = !!d.current && !!d.serviceClass && d.vehicles.some((v) => v.vehicleId === d.current!.vehicleId && v.serviceClass !== d.serviceClass);
  const changed = !d.current || d.current.outdated || d.current.employeeId !== employeeId || d.current.vehicleId !== vehicleId || d.current.startsAt !== startsAt || d.current.endsAt !== endsAt;
  const ready = !!employeeId && !!vehicleId && !!startsAt && !!endsAt && endsAt > startsAt;
  const mailHint = !d.mail.customerEmail ? 'Der Kunde hat keine E-Mail-Adresse, Sie informieren ihn selbst.' : !d.mail.senderReady ? 'Der E-Mail-Versand ist noch nicht eingerichtet (Einrichtung → APIs und Webhooks), Sie informieren den Kunden selbst.' : null;

  return (
    <section className="card dispo">
      <h2>Disposition</h2>
      <p className="muted small">
        Weisen Sie den Auftrag einem Fahrer und einem Fahrzeug zu. Zur Wahl stehen alle aktiven Mitarbeiter. Hat der Mitarbeiter am Tag der Abholung keine Schicht, legt Wirkia sie mit dem Zuweisen an. Mit dem Zuweisen wird der Auftrag „Geplant“ und der Kunde bekommt per E-Mail die Bestätigung
        und die Angaben zu Fahrer und Fahrzeug.
      </p>
      {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--warn'}`} role={notice.ok ? 'status' : 'alert'}>{notice.text}</div>}

      {!d.current && d.driver && (
        <div className="alert alert--ok" role="status">
          <strong>{d.driver.name}</strong> {d.driver.claimedAt ? 'hat die Fahrt selbst angenommen' : 'ist als Fahrer eingetragen'}. Es fehlt noch das Fahrzeug: Wählen Sie es und klicken Sie auf „Zuweisen und planen“, dann bekommt der Kunde die Angaben zu Fahrer und Fahrzeug.
        </div>
      )}
      {d.current && (
        <p className="dispo-current">
          {d.current.outdated ? 'Bisher zugewiesen' : 'Zugewiesen'}: <strong>{d.current.employeeName ?? 'ohne Fahrer'}</strong> mit <strong>{d.current.vehiclePlate}</strong>, {dayDe(d.current.startsAt.slice(0, 10))} {d.current.startsAt.slice(11)} bis {d.current.endsAt.slice(11)} Uhr.
          {d.mail.confirmationSentAt && <span className="muted small"> Bestätigung am {stamp.format(new Date(d.mail.confirmationSentAt))} Uhr an den Kunden.</span>}
          {d.mail.driverInfoSentAt && <span className="muted small"> Fahrerangaben am {stamp.format(new Date(d.mail.driverInfoSentAt))} Uhr.</span>}
        </p>
      )}

      {d.current?.outdated && d.pickupDate && (
        <div className="alert alert--warn" role="status">
          Das Leistungsdatum ist jetzt der {dayDe(d.pickupDate)}, die Zuweisung liegt noch am {dayDe(d.current.startsAt.slice(0, 10))}. Wählen Sie Fahrer und Fahrzeug für den neuen Tag. Hat der Kunde die Bestätigung schon bekommen, teilen Sie ihm den neuen Termin mit.
        </div>
      )}
      {currentMismatch && d.current && d.serviceClass && (
        <div className="alert alert--warn" role="status">
          Das zugewiesene Fahrzeug {d.current.vehiclePlate} gehört nicht zur verlangten Klasse {SERVICE_CLASS_LABELS[d.serviceClass]}. Wählen Sie ein passendes Fahrzeug und weisen Sie neu zu.
        </div>
      )}
      {!d.pickupDate && <div className="alert alert--warn" role="status">Für diesen Auftrag ist kein Abholdatum bekannt. Tragen Sie es im Auftrag unter „Leistungsdatum“ ein, dann zeigt Wirkia die Fahrer mit Schicht an diesem Tag.</div>}

      <div className="form-grid">
        <label className="field">
          <span>Verlangte Serviceklasse</span>
          <select className="input" value={d.serviceClass ?? ''} disabled={setClass.isPending || d.started} onChange={(e) => setClass.mutate((e.target.value || null) as ServiceClass | null)}>
            <option value="">Keine Anforderung</option>
            {SERVICE_CLASSES.map((k) => <option key={k} value={k}>{SERVICE_CLASS_LABELS[k]}</option>)}
          </select>
          <small className="muted">{d.serviceClass ? `Zur Wahl stehen nur Fahrzeuge der Klasse ${SERVICE_CLASS_LABELS[d.serviceClass]}.` : 'Ohne Anforderung stehen alle Fahrzeuge zur Wahl.'} {d.started ? 'Die Fahrt hat begonnen, die Klasse ist gesperrt.' : `Sie können die Klasse bis zum Start der Fahrt ändern, auch wenn der Kunde sie anders angefragt hat.${d.current ? ' Ein zugewiesenes Fahrzeug wird dabei herausgenommen, Sie wählen danach ein passendes.' : ''}`}</small>
        </label>
        <label className="field">
          <span>Fahrer</span>
          <select className="input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} disabled={d.drivers.length === 0}>
            <option value="">– bitte wählen –</option>
            {d.drivers.map((x) => (
              <option key={x.employeeId} value={x.employeeId} disabled={x.onLeave}>{x.name} · {x.shift ? `Schicht ${x.shift.start}–${x.shift.end}${x.shift.published ? '' : ' (Entwurf)'}` : 'ohne Schicht'}{x.onLeave ? ' · abwesend' : x.online ? ' · online' : ''}{x.busy ? ' · hat schon einen Auftrag' : ''}{x.shift && !x.coversPickup && d.pickupTime ? ' · Abholung außerhalb der Schicht' : ''}</option>
            ))}
          </select>
          {driver && !driver.shift && <small className="muted">{driver.name} hat am Tag der Abholung keine Schicht. Mit dem Zuweisen legt Wirkia sie für die Zeit des Einsatzes an und trägt den Auftrag als Aufgabe im Kalender ein.</small>}
          {driver && driver.shift && !driver.coversPickup && d.pickupTime && <small className="muted">Die Abholung um {d.pickupTime} Uhr liegt außerhalb der Schicht von {driver.shift.start} bis {driver.shift.end} Uhr. Wirkia prüft nur, dass sich die Einsätze nicht überlappen.</small>}
          {driver && driver.busy && <small className="muted">Der Fahrer hat zu dieser Zeit schon einen anderen Auftrag. Wirkia warnt Sie beim Zuweisen, Sie können trotzdem bestätigen.</small>}
        </label>
        <label className="field">
          <span>Fahrzeug</span>
          <select className="input" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">– bitte wählen –</option>
            {d.vehicles.map((x) => (
              <option key={x.vehicleId} value={x.vehicleId} disabled={!x.free && x.vehicleId !== d.current?.vehicleId}>{x.plate} · {x.label}{x.color ? `, ${x.color}` : ''}{x.serviceClass ? ` · ${SERVICE_CLASS_LABELS[x.serviceClass]}` : ''}{x.reason ? ` · ${x.reason}` : ''}{x.occupiedBy ? ` · belegt durch ${x.occupiedBy}` : ''}</option>
            ))}
          </select>
          {d.serviceClass && d.vehicles.filter((v) => v.free).length === 0 && <small className="muted">Kein freies Fahrzeug der Klasse {SERVICE_CLASS_LABELS[d.serviceClass]} zu dieser Zeit. Ändern Sie die Zeit oder die Klasse, oder ordnen Sie unter Fuhrpark → Flotte ein Fahrzeug dieser Klasse zu.</small>}
          {vehicle?.occupiedBy && <small className="fleet-warn">Dieses Fahrzeug ist gerade von {vehicle.occupiedBy} belegt (in Wirkia Drivers gewählt, online). Sie können es trotzdem zuweisen, Wirkia fragt vorher nach.</small>}
          {vehicle && !vehicle.color && <small className="muted">Für dieses Fahrzeug ist keine Farbe hinterlegt. Der Kunde sieht sie in seiner E-Mail, tragen Sie sie in der Fahrzeugakte ein.</small>}
        </label>
        <label className="field">
          <span>Beginn</span>
          <input className="input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </label>
        <label className="field">
          <span>Ende</span>
          <input className="input" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </label>
      </div>

      <label className="check">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        <span>Kunden per E-Mail informieren{d.mail.customerEmail ? ` (${d.mail.customerEmail})` : ''}</span>
      </label>
      {notify && mailHint && <small className="muted">{mailHint}</small>}

      <div className="form-actions">
        <button className="btn btn--primary" type="button" disabled={!ready || !changed || assign.isPending} onClick={() => { setNotice(null); assign.mutate(false); }}>
          {assign.isPending ? 'Wird zugewiesen …' : d.current ? 'Zuweisung ändern' : 'Zuweisen und planen'}
        </button>
      </div>
    </section>
  );
}
